import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { buildCognitoLogoutUrl } from '@/auth/sign-out';

export function NavHeader() {
  const auth = useAuth();
  const isSignedIn = auth.isAuthenticated;

  const profile = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
    enabled: isSignedIn,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 1;
    },
  });

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-sm">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="text-lg font-bold tracking-tight">
          🐟 Fishymeter
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          {!isSignedIn && !auth.isLoading && (
            <button
              type="button"
              onClick={() => auth.signinRedirect()}
              className="rounded-md bg-primary px-3 py-1.5 font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Sign in
            </button>
          )}
          {isSignedIn && (
            <UserMenu
              username={profile.data?.username}
              loading={profile.isLoading}
              missingProfile={
                profile.error instanceof ApiError && profile.error.status === 404
              }
            />
          )}
        </nav>
      </div>
    </header>
  );
}

function UserMenu({
  username,
  loading,
  missingProfile,
}: {
  username?: string;
  loading: boolean;
  missingProfile: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 font-medium hover:bg-muted"
      >
        {loading ? '…' : missingProfile ? 'Welcome' : username ? `@${username}` : 'You'}
        <span className="text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 rounded-md border border-border bg-card py-1 shadow-md">
          {missingProfile ? (
            <Link
              to="/welcome"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm hover:bg-muted"
            >
              Claim username
            </Link>
          ) : (
            <Link
              to="/my-catches"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm hover:bg-muted"
            >
              My catches
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              window.localStorage.clear();
              window.location.href = buildCognitoLogoutUrl();
            }}
            className="block w-full px-3 py-2 text-left text-sm text-destructive hover:bg-muted"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
